import React, { useEffect, useReducer } from "react";

import client from "@culturehq/client";
import { Button } from "@culturehq/components";

import { useProfile } from "app/context";
import { Trans } from "app/utils";

import Carousel from "components/common/Carousel";
import TrendAnchor from "components/trends/TrendAnchor";
import PostStory from "components/stories/post-stories-v2/PostStory";
import styles from "./story-slider.module.scss";

const queryToOptions = queryString => {
  const { i, d, l, s, cl, cy, p, de, v } = queryString;

  return {
    interestIds: i || null,
    departmentIds: d || null,
    locationIds: l || null,
    skillIds: s || null,
    clubIds: cl || null,
    classYearIds: cy || null,
    programIds: p || null,
    degreeIds: de || null,
    organizationValueIds: v || null
  };
};

const initialState = {
  appending: null,
  error: null,
  fetching: false,
  pagination: {},
  stories: [],
  options: {},
  queryString: ""
};

const reducer = (state, action) => {
  switch (action.type) {
    case "appendStart":
      return { ...state, appending: state.pagination.currentPage + 1 };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        stories: [...state.stories, ...action.response.stories]
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        pagination: action.response.pagination,
        stories: action.response.stories
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    case "updateOptions":
      return {
        ...state,
        options: queryToOptions(action.queryString),
        queryString: action.queryString
      };
    default:
      return state;
  }
};

const useFetchStories = (dispatch, options, ignoreTrending) => useEffect(() => {
  let cancelled = false;
  dispatch({ type: "fetchStart" });

  client
    .getTrendingStoryQuestion({ page: 0, ...options, ignoreTrending })
    .then(response => {
      if (!cancelled) {
        dispatch({ type: "fetchSuccess", response });
      }
    })
    .catch(error => {
      if (!cancelled) {
        dispatch({ type: "fetchError", error });
      }
    });

  return () => {
    cancelled = true;
  };
}, [dispatch, options, ignoreTrending]);

const useAppendStories = (dispatch, appending, options, ignoreTrending) => useEffect(() => {
  if (appending) {
    let cancelled = false;

    client
      .getTrendingStoryQuestion({
        page: appending,
        ...options,
        ignoreTrending
      })
      .then(response => {
        if (!cancelled) {
          dispatch({ type: "appendSuccess", response });
        }
      })
      .catch(error => {
        if (!cancelled) {
          dispatch({ type: "appendError", error });
        }
      });

    return () => {
      cancelled = true;
    };
  }

  return undefined;
}, [dispatch, appending, options, ignoreTrending]);

const ViewAllStoryTrends = props => (
  <div className="view-all-story-trends">
    <TrendAnchor {...props} />
  </div>
);

const TrendingStoriesSlider = ({
  forYou,
  openModal,
  ignoreTrending,
  isProfile,
  queryString,
  onPosted,
  onStoryClick,
  selectedStory,
  showPoints
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { profile } = useProfile();
  const { createYourOwnStory } = profile.organization;

  useEffect(() => {
    if (queryString && !isQueryStringEmpty(queryString)) {
      dispatch({ type: "updateOptions", queryString });
    }
  }, [queryString]);

  useFetchStories(dispatch, state.options, ignoreTrending);
  useAppendStories(dispatch, state.appending, state.options, ignoreTrending);

  const isQueryStringEmpty = opts => {
    const Found = {};
    try {
      Object.keys(opts).forEach(key => {
        if (opts[key]) {
          throw Found;
        }
      });
    } catch (_) {
      return false;
    }

    return true;
  };

  const handleAppendData = () => {
    dispatch({ type: "appendStart" });
  };

  if (state.fetching || !(!isQueryStringEmpty(state.options) || forYou || isProfile)) {
    return <></>;
  }

  return (
    <Carousel
      appending={state.appending}
      carouselFooter={<ViewAllStoryTrends isProfile={isProfile} />}
      data={state.stories}
      onAppendData={handleAppendData}
      pagination={state.pagination}
    >
      {(!isQueryStringEmpty(state.options) || forYou || isProfile)
        && createYourOwnStory && (
        <PostStory
          openModal={openModal}
          queryString={queryString}
          selectedStory={selectedStory}
          showPoints={showPoints}
          startYourTrend
        />
      )}
      {state.stories.map(story => (
        <PostStory
          key={story.id}
          isTrendingStory
          isProfile={isProfile}
          preFilledQuestion={story}
          openModal={openModal}
          onPosted={onPosted}
          onStoryClick={onStoryClick}
          selectedStory={selectedStory}
          showPoints={showPoints}
        />
      ))}
      {state.stories.length === 0 && !state.fetching && (
        <div
          className={`${styles.emptyContainerDescription} Trending-Stories__Empty`}
        >
          <div>
            <h3 className={styles.emptyTitle}>
              <span role="img" aria-label="thunder">
                <svg
                  className={styles.emptyIcon}
                  xmlns="http://www.w3.org/2000/svg"
                  id="Capa_1"
                  enableBackground="new 0 0 512.002 512.002"
                  height="512px"
                  viewBox="0 0 512.002 512.002"
                  width="512px"
                >
                  <g>
                    <path
                      d="m201.498 512.002c-1.992 0-4.008-.398-5.934-1.229-6.741-2.907-10.387-10.266-8.617-17.39l50.724-204.178h-136.67c-4.947 0-9.576-2.439-12.373-6.52s-3.402-9.278-1.617-13.892l100.262-259.204c2.235-5.779 7.793-9.589 13.989-9.589h137.961c5.069 0 9.795 2.56 12.565 6.806 2.768 4.246 3.206 9.603 1.162 14.242l-59.369 134.76h117.42c5.486 0 10.534 2.995 13.164 7.81 2.63 4.814 2.422 10.68-.543 15.296l-209.496 326.192c-2.833 4.412-7.651 6.896-12.628 6.896z"
                      fill="#ffe645"
                      data-original="#FFE645"
                      style={{ fill: "#FFD24B" }}
                      data-old_color="#ffe645"
                    />
                    <path
                      d="m411.001 155.807h-117.42l59.369-134.76c2.044-4.639 1.606-9.996-1.162-14.242-2.769-4.245-7.495-6.805-12.564-6.805h-73.237v424.357l157.636-245.444c2.965-4.616 3.173-10.482.543-15.296-2.631-4.815-7.678-7.81-13.165-7.81z"
                      fill="#ffcf2c"
                      data-original="#FFCF2C"
                      className="active-path"
                      style={{ fill: "#E2BA42" }}
                      data-old_color="#ffcf2c"
                    />
                  </g>
                </svg>
              </span>
              <Trans>Be a trendsetter and share the first story</Trans>
            </h3>
          </div>
          <p className={styles.emptyDescription}>
            <Trans>
              You can get things started by sharing a story and using these
              tags!
            </Trans>
          </p>
          {createYourOwnStory && (
            <PostStory queryString={queryString}>
              {onTrigger => (
                <Button onClick={onTrigger} inverted>
                  <Trans>Start the trend</Trans>
                </Button>
              )}
            </PostStory>
          )}
        </div>
      )}
    </Carousel>
  );
};

export default TrendingStoriesSlider;
